import { useEffect, useState } from 'react';
import { FormProvider, useFieldArray, useForm } from 'react-hook-form';
import { useNavigate } from 'react-router';
import { useParams, useOutletContext } from 'react-router-dom';
import { yupResolver } from '@hookform/resolvers/yup';
import { makeStyles } from 'tss-react/mui';
import moment from 'moment';
import { useCaseContext } from './CaseContext';

import DoNotDisturbIcon from '@mui/icons-material/DoNotDisturb';
import PaymentsIcon from '@mui/icons-material/Payments';
import ReceiptIcon from '@mui/icons-material/Receipt';
import RuleIcon from '@mui/icons-material/Rule';
import DrawIcon from '@mui/icons-material/Draw';
import DocumentScannerIcon from '@mui/icons-material/DocumentScanner';
import MobileScreenShareIcon from '@mui/icons-material/MobileScreenShare';
import CreditCardIcon from '@mui/icons-material/CreditCard';
// import ArchiveIcon from '@mui/icons-material/Archive';
import DeleteForeverIcon from '@mui/icons-material/DeleteForever';
import SaveIcon from '@mui/icons-material/Save';
import { Box, IconButton, Theme, useTheme, Typography } from '@mui/material';

import {
  handleGetCaseTypes,
  handleGetDefendants,
  handleGetPlaintiffs,
  handleGetTrustAccounts,
  mapCaseTypesQueryResults,
  mapDefendantsQueryResults,
  mapPlaintiffsQueryResults,
  mapTrustAccountsQueryResults,
} from './helpers';
import { CaseEditFormSubmitProps, DefendantField, SecondaryFirmsField } from './types';

import { abandonCase, deleteCase, submitCase, updateCase } from '../../api/casesApi/casesApi';
import Loader from '../../components/Loader';
import FormCancelButton from '../../components/buttons/FormCancelButton';
import FormSubmitButton from '../../components/buttons/FormSubmitButton';
import FormDateField from '../../components/formFields/FormDateField';
import FormTextField from '../../components/formFields/FormTextField';
import FormSelectField from '../../components/formFields/FormSelectField';
import { CASES_EDIT_FORM_DEFAULT_VALUES } from '../../helpers/formSchemas/formDefaultValues';
import { casesEditFormSchema } from '../../helpers/formSchemas/formSchemas';
import AddButton from '../../components/buttons/AddButton';
import FormAutocompleteFieldServerSearch from '../../components/formFields/FormAutocompleteFieldServerSearch';
import FormCopyTextField from '../../components/formFields/FormCopyTextField';
import FormLinkButton from '../../components/buttons/FormLinkButton';
import CommonButton from '../../components/buttons/CommonButton';
import usePageTitle from '../../hooks/usePageTitle';
import useSnackBar from '../../hooks/useSnackBar';
import useWindowWidth from '../../hooks/useWindowWidth';
import {
  CaseTypeCategory,
  CaseTypeData,
  CounselRole,
  EntityFlexibleRecord,
  SelectOption,
} from '../../types';
import { getClientRecord, resetClientAccount } from '../../api/clientsApi/clientsApi';
import { useHasPermission } from '../../hooks/useHasPermission';
import {
  CASE_TYPES_CATEGORIES_OPTIONS,
} from '../../constants/constants';
import Popup from '../../components/Popup';
import { POPUP_DESCRIPTIONS } from '../../constants/popupDescriptions';

const useStyles = makeStyles()((theme: Theme) => ({
  pageContainer: {
    display: 'flex',
  },
  formContainer: {
    display: 'flex',
    flexBasis: '1184px',

    '@media (max-width: 950px)': {
      flexBasis: '480px',
    },
  },
  formWrapper: {
    display: 'flex',
    gap: '64px',

    '@media (max-width: 1200px)': {
      flexDirection: 'column',
      gap: '32px',
    },
  },
  formInnerWrapper: {
    display: 'flex',
    flexDirection: 'column',
    padding: '32px',
    backgroundColor: theme.palette.primary.light,
    borderRadius: '16px',
    flexBasis: '896px',

    '@media (max-width: 1200px)': {
      flexBasis: '416px',
    },

    '@media (max-width: 950px)': {
      padding: '12px',
    },
  },
  formInfoContainer: {
    display: 'flex',
    width: '100%',
    gap: '24px',

    '@media (max-width: 950px)': {
      flexDirection: 'column',
    },
  },
  formPlaceInfoContainer: {
    marginTop: '16px',
    display: 'flex',
    gap: '16px',

    '@media (max-width: 1200px)': {
      flexDirection: 'column',
    },
  },
  defendantFieldsContainer: {
    display: 'flex',
    alignItems: 'center',
    gap: '16px',
    marginTop: '16px',

    '@media (max-width: 1200px)': {
      flexDirection: 'column',
    },
  },
  dateFieldsContainer: {
    display: 'flex',
    alignItems: 'center',
    gap: '24px',
    marginTop: '16px',
    flexDirection: 'column',
  },
  fieldHeadline: {
    fontWeight: 600,
    fontSize: '20px',
    lineHeight: '24px',
    color: theme.palette.common.white,
    fontFamily: 'Inter',
  },
}));

const CasesEditFormContainer = () => {
  const styles = useStyles();
  const theme = useTheme();
  const windowWidth = useWindowWidth();
  const navigate = useNavigate();
  const { caseId } = useParams();
  const { setPageTitle } = usePageTitle();
  const { setAlert } = useSnackBar();
  const { entityData } = useOutletContext<EntityFlexibleRecord>();

  const firmId = localStorage.getItem('firmId');

  // Secondary firms may view a case but may not edit, submit, abandon or delete it.
  const isLeadFirm = entityData?.isLeadFirm !== false;

  const canEdit = useHasPermission('Cases', 'Edit') && isLeadFirm;
  const canDelete = useHasPermission('Cases', 'Delete') && isLeadFirm;
  const canViewClients = useHasPermission('Clients', 'View') && isLeadFirm;
  const canCreateClients = useHasPermission('Clients', 'Create') && isLeadFirm;
  const canViewDefendants = useHasPermission('Defendants', 'View') && isLeadFirm;
  const canCreateDefendants = useHasPermission('Defendants', 'Create') && isLeadFirm;

  const { refetchCase } = useCaseContext();

  const [isFormSubmitting, setIsFormSubmitting] = useState(false);
  const [currentCaseStatus, setCurrentCaseStatus] = useState<string>('');
  const [isAbandonCasePopupOpen, setIsAbandonCasePopupOpen] = useState<boolean>(false);
  const [isDeleteCasePopupOpen, setIsDeleteCasePopupOpen] = useState<boolean>(false);
  const [clientConfirmations, setClientConfirmations] = useState({
    email: false,
    ssn: false,
    dob: false,
  });

  const formMethods = useForm({
    mode: 'onChange',
    reValidateMode: 'onChange',
    resolver: yupResolver(casesEditFormSchema),
    defaultValues: CASES_EDIT_FORM_DEFAULT_VALUES,
  });

  const {
    control,
    handleSubmit,
    reset,
    setValue,
    watch,
    formState: { isValid, isDirty },
  } = formMethods;

  const clientFieldValue = watch('clientId');
  const defendantsFieldValue = watch('defendant');
  const plaintiffId = watch('clientId.id');
  const caseTypeCategory = watch('caseTypeCategory');
  const type = watch('type');
  const isDraft = currentCaseStatus === 'Draft';
  const isActive = currentCaseStatus === 'Active';
  const isClosed = currentCaseStatus === 'Closed';
  const isAbandon = currentCaseStatus === 'Abandoned';
  const canAbandon = entityData?.canBeAbandoned;
  const paymentStarted = watch('paymentStarted');
  const trustAcc = watch('trustAccount.id');
  const isSendAppInviteAllowed = Object.values(clientConfirmations).every(
    (confirmation) => confirmation,
  );

  const { fields, append, remove } = useFieldArray({
    name: 'defendant',
    control,
  });

  const handleAddDefendant = () => {
    append({
      defendantId: { id: '', label: '' },
      gross: '',
    });
  };

  const onSubmitForm = (values: CaseEditFormSubmitProps) => {
    setIsFormSubmitting(true);

    const mappedDefendants = values?.defendant?.map((defendant: DefendantField) => ({
      id: Number(defendant.defendantId.id),
      grossSettlement: defendant.gross ? Number(defendant.gross) : null,
    }));

    const mappedFirms = values?.secondaryFirms?.map((firm: SecondaryFirmsField) => ({
      id: Number(firm?.firmId?.id),
      role: firm.firmRole as CounselRole,
    }));

    const submitData = {
      referenceId: values.referenceId,
      ...(mappedFirms && { secondaryFirms: mappedFirms }),
      clientId: Number(values.clientId.id),
      ...(mappedDefendants && { defendants: mappedDefendants }),
      caseTypeId: Number(values.type.id),
      ...(values.filedAt ? { filedAt: moment(values.filedAt).format('MM-DD-YYYY') } : { filedAt: null }),
      ...(values.sol ? { sol: moment(values.sol).format('MM-DD-YYYY') } : { sol: null }),
      ...(values.trustAccount?.id && { trustAccountId: Number(values.trustAccount.id) }),
    };

    updateCase(Number(caseId), submitData)
      .then(() => {
        setIsFormSubmitting(false);
        refetchCase();
        // navigate('/cases');

        setAlert((prev) => ({
          ...prev,
          message: 'Case updated successfully.',
          type: 'success',
        }));
      })
      .catch((err) => {
        setIsFormSubmitting(false);
        setAlert((prev) => ({
          ...prev,
          message: err.response.data.message,
          type: 'error',
        }));
      });
  };

  const handleSubmitCase = () => {
    submitCase(Number(caseId))
      .then(() => {
        navigate('/cases');
        setAlert((prev) => ({
          ...prev,
          message: 'Case submitted successfully.',
          type: 'success',
        }));
      })
      .catch((err) => {
        setAlert((prev) => ({
          ...prev,
          message: err.response.data.message,
          type: 'error',
        }));
      });
  };

  const handleAbandonCase = () => {
    abandonCase(Number(caseId))
      .then(() => {
        navigate('/cases');
        setAlert((prev) => ({
          ...prev,
          message: 'Case abandoned successfully.',
          type: 'success',
        }));
      })
      .catch((err) => {
        setAlert((prev) => ({
          ...prev,
          message: err.response.data.message,
          type: 'error',
        }));
      });
  };

  const handleDeleteCase = () => {
    deleteCase(Number(caseId))
      .then(() => {
        navigate('/cases');
        setAlert((prev) => ({
          ...prev,
          message: 'Case deleted successfully.',
          type: 'success',
        }));
      })
      .catch((err) => {
        setAlert((prev) => ({
          ...prev,
          message: err.response.data.message,
          type: 'error',
        }));
      });
  };

  const handleCancelChanges = () => reset();

  const handleResetClientAccount = () => {
    resetClientAccount(plaintiffId as string)
      .then(() => {
        setAlert((prev) => ({
          ...prev,
          message: 'App invitation has been sent successfully.',
          type: 'success',
        }));
      })
      .catch((err) => {
        setAlert((prev) => ({
          ...prev,
          message: err.response.data.message,
          type: 'error',
        }));
      });
  };

  const handleChangeCaseTypeCategoryValue = (value: string) => {
    setValue('caseTypeCategory', value, { shouldDirty: true, shouldValidate: true });
    setValue('type', { id: '', label: '' }, { shouldDirty: true, shouldValidate: true });
    setValue('trustAccount', { id: '', label: '' }, { shouldDirty: true, shouldValidate: true });
  };

  const handleChangeCaseTypeValue = (value: SelectOption | null) => {
    if (!value) {
      setValue('trustAccount', { id: '', label: '' }, { shouldDirty: true, shouldValidate: true });
      return;
    }
    // Fetch case types to get trustAccounts data
    handleGetCaseTypes({
      page: 1,
      size: 100,
      count: true,
      match: '',
      filters: {
        category: caseTypeCategory,
        ...(caseTypeCategory === CaseTypeCategory.Custom && { firmId: Number(firmId) }),
      },
    }).then((res) => {
      const caseTypesData = res?.data?.resultSet || [];
      const caseType = caseTypesData.find((ct: CaseTypeData) => ct.id === Number(value.id));

      if (caseType && caseType.trustAccounts && caseType.trustAccounts.length) {
        const trustAccount = caseType.trustAccounts.find((ta) => ta.firmId === Number(firmId));
        if (trustAccount) {
          setValue('trustAccount', { id: trustAccount.id.toString(), label: trustAccount.name }, { shouldDirty: true, shouldValidate: true });
        }
      }
    });
  };

  useEffect(() => {
    if (entityData) {
      reset({
        ...entityData,
      });

      const clientId = entityData?.clientId as { id: string; label: string };

      getClientRecord(clientId.id as string).then((res) => {
        if (res.data) {
          setClientConfirmations((...prevState) => ({
            ...prevState,
            email: res.data.emailConfirmed,
            ssn: res.data.ssnConfirmed,
            dob: res.data.dobConfirmed,
          }));
        }
      });

      setCurrentCaseStatus(entityData.status as string);
      setPageTitle && setPageTitle(entityData.name as string);
    }

    return () => setPageTitle && setPageTitle('');
  }, [entityData]);

  return (
    <>
      {!entityData ? (
        <Loader colorType="warning" />
      ) : (
        <>
          <Box className={styles.classes.pageContainer}>
            <FormProvider {...formMethods}>
              <form className={styles.classes.formContainer}>
                <Box className={styles.classes.formInfoContainer}>
                  <Box className={styles.classes.formInnerWrapper}>
                    <Box className={styles.classes.formWrapper}>
                      <Box display="flex" flexDirection="column" width="100%" flexBasis="416px">
                        <Typography className={styles.classes.fieldHeadline}>Identity</Typography>
                        <Box mt="8px">
                          <FormCopyTextField entityName="Case" name="id" label="Id" isReadOnly />
                        </Box>
                        <Box mt="24px">
                          <FormTextField name="name" label="System Name" readOnly isReadOnly />
                        </Box>
                        <Box mt="24px">
                          <FormTextField
                            name="referenceId"
                            label="Reference Id *"
                            disabled={!isDraft || !canEdit}
                          />
                        </Box>
                        <Box mt="24px">
                          <FormSelectField
                            disabled={!isDraft || !canEdit}
                            options={CASE_TYPES_CATEGORIES_OPTIONS}
                            name="caseTypeCategory"
                            label="Case Type Category *"
                            width="100%"
                            customOnChange={handleChangeCaseTypeCategoryValue}
                          />
                        </Box>
                        <Box mt="24px">
                          <FormAutocompleteFieldServerSearch
                            key={`${caseTypeCategory ?? 'none'}-${firmId ?? 'no-firm'}`}
                            disabled={!isDraft || !caseTypeCategory || !canEdit}
                            name="type"
                            label="Case Type *"
                            width="100%"
                            filters={{
                              category: caseTypeCategory,
                              ...(caseTypeCategory === CaseTypeCategory.Custom && {
                                firmId: Number(firmId),
                              }),
                            }}
                            optionsQuery={handleGetCaseTypes}
                            mapQueryResults={mapCaseTypesQueryResults}
                            isOptionEqualToValue={(option, value) => String(option.id) === String(value.id)}
                            onValueChange={handleChangeCaseTypeValue}
                            fetchSize={100}
                            minSearchChars={3}
                          />
                        </Box>

                        {isLeadFirm && (
                          <>
                            <Typography className={styles.classes.fieldHeadline} mt="32px">
                              Trust Account
                            </Typography>
                            <Box mt="16px">
                              <FormAutocompleteFieldServerSearch
                                key={type?.id ? String(type.id) : 'no-type'}
                                disabled={!type?.id || paymentStarted || !canEdit}
                                label="Trust Account *"
                                name="trustAccount"
                                width="100%"
                                optionsQuery={handleGetTrustAccounts}
                                mapQueryResults={mapTrustAccountsQueryResults}
                                isOptionEqualToValue={(option, value) => String(option.id) === String(value.id)}
                                isDisableClearable={!trustAcc}
                                fetchSize={100}
                                minSearchChars={3}
                              />
                            </Box>
                          </>
                        )}

                        <Typography className={styles.classes.fieldHeadline} mt="32px">
                          Key Dates
                        </Typography>
                        <Box className={styles.classes.dateFieldsContainer}>
                          <Box width="100%">
                            <FormDateField name="filedAt" label="Date Filed" disabled={!isDraft || !canEdit} />
                          </Box>
                          <Box width="100%">
                            <FormDateField name="sol" label="SOL Date" disabled={!isDraft || !canEdit} />
                          </Box>
                        </Box>
                      </Box>

                      <Box display="flex" flexDirection="column" width="100%" flexBasis="416px">
                        <Box display="flex" alignItems="flex-end" justifyContent="space-between">
                          <Typography className={styles.classes.fieldHeadline}>
                            Plaintiff
                          </Typography>
                          {canViewClients && (
                            <FormLinkButton
                              path={`/clients/${clientFieldValue && clientFieldValue.id}/home`}
                              disabled={!clientFieldValue}
                            />
                          )}
                        </Box>
                        <Box mt="8px">
                          <FormAutocompleteFieldServerSearch
                            disabled={!isDraft || !canEdit}
                            name="clientId"
                            label="Client *"
                            optionsQuery={handleGetPlaintiffs}
                            mapQueryResults={mapPlaintiffsQueryResults}
                            filters={{
                              firmId: Number(firmId),
                            }}
                            isOptionEqualToValue={(option, value) => String(option.id) === String(value.id)}
                            fetchSize={100}
                            minSearchChars={3}
                          />
                        </Box>
                        {canCreateClients && (
                          <Box mt="16px" display="flex">
                            <AddButton buttonText="Create new client" path="/clients/create-client" />
                          </Box>
                        )}

                        <Box display="flex" alignItems="flex-end" justifyContent="space-between">
                          <Typography className={styles.classes.fieldHeadline} mt="32px">
                            Defendants
                          </Typography>
                          {canViewDefendants && (
                            <FormLinkButton
                              path={`/defendants?caseId=${caseId}`}
                              disabled={
                                !(defendantsFieldValue && defendantsFieldValue[0]?.defendantId?.id)
                              }
                            />
                          )}
                        </Box>

                        {fields.map((field, idx) => (
                          <Box className={styles.classes.defendantFieldsContainer} key={field.id}>
                            <FormAutocompleteFieldServerSearch
                              name={`defendant.${idx}.defendantId`}
                              label="Defendant *"
                              width="100%"
                              maxWidth="100%"
                              disabled={!isDraft || !canEdit}
                              optionsQuery={handleGetDefendants}
                              mapQueryResults={mapDefendantsQueryResults}
                              filters={{
                                firmId: Number(firmId),
                              }}
                              isOptionEqualToValue={(option, value) => String(option.id) === String(value.id)}
                              fetchSize={100}
                              minSearchChars={3}
                            />

                            {/* <FormTextField
                              name={`defendant.${idx}.gross`}
                              label="Gross Settlement"
                              maxWidth={windowWidth <= 1200 ? '100%' : '180px'}
                              disabled={!isDraft || !canEdit}
                            /> */}

                            {idx >= 1 ? (
                              <Box
                                display="flex"
                                width={windowWidth <= 1200 ? '100%' : 'inherit'}
                                gap="16px"
                              >
                                <IconButton
                                  sx={{
                                    padding: 0,
                                    color: theme.palette.error.main,

                                    '&.Mui-disabled': {
                                      color: 'rgba(255, 255, 255, 0.08)',
                                    },
                                  }}
                                  onClick={() => remove(idx)}
                                  disabled={!isDraft || !canEdit}
                                >
                                  <DeleteForeverIcon />
                                </IconButton>
                              </Box>
                            ) : null}
                          </Box>
                        ))}

                        <Box
                          mt="16px"
                          display="flex"
                          flexDirection="column"
                          justifyContent="space-between"
                          gap="16px"
                        >
                          {canCreateDefendants && (
                            <AddButton
                              buttonText="Create new defendant"
                              path="/defendants/create-defendant"
                            />
                          )}
                          {isLeadFirm && (
                            <AddButton
                              buttonText="Add defendant"
                              handleClick={handleAddDefendant}
                              disabled={!isDraft || !canEdit}
                            />
                          )}
                        </Box>

                        {/* <Typography className={styles.classes.fieldHeadline} mt="32px">
                          Firms
                        </Typography>

                        {secondaryFirmsFields.map((field, idx) => (
                          <Box className={styles.classes.defendantFieldsContainer} key={field.id}>

                            <FormAutocompleteFieldServerSearch
                              name={`secondaryFirms.${idx}.firmId`}
                              label="Firm *"
                              width="100%"
                              minWidth="180px"
                              maxWidth={windowWidth <= 1200 ? '100%' : '272px'}
                              disabled={!isDraft || !canEdit}
                              optionsQuery={handleGetFirms}
                              mapQueryResults={(array) => mapFirmsQueryResults(array, JSON.parse(localStorage.getItem('firmId') || '0'),)}
                              isOptionEqualToValue={(option, value) => String(option.id) === String(value.id)}
                              fetchSize={100}
                              minSearchChars={3}
                            />

                            <Box
                              display="flex"
                              width={windowWidth <= 1200 ? '100%' : 'inherit'}
                              gap="16px"
                            >
                              <FormSelectField
                                name={`secondaryFirms.${idx}.firmRole`}
                                label="Role"
                                maxWidth={windowWidth <= 1200 ? '100%' : '180px'}
                                options={SECONDARY_FIRM_ROLES_OPTIONS}
                                width={windowWidth <= 1200 ? '100%' : 'auto'}
                                {...(windowWidth <= 1200 && { minWidth: 'unset' })}
                                disabled={!isDraft || !canEdit}
                              />
                              <IconButton
                                sx={{
                                  padding: 0,
                                  color: theme.palette.error.main,

                                  '&.Mui-disabled': {
                                    color: 'rgba(255, 255, 255, 0.08)',
                                  },
                                }}
                                onClick={() => secondaryFirmRemove(idx)}
                                disabled={!isDraft || !canEdit}
                              >
                                <DeleteForeverIcon />
                              </IconButton>
                            </Box>
                          </Box>
                        ))} */}

                        {/* <Box
                          mt="16px"
                          display="flex"
                          flexDirection="column"
                          justifyContent="space-between"
                          maxWidth="468px"
                          gap="16px"
                        >
                          <AddButton
                            buttonText="Add firm"
                            handleClick={handleAddSecondaryFirm}
                            disabled={!isDraft || !canEdit}
                          />
                        </Box> */}
                      </Box>
                    </Box>

                    {canEdit && (
                      <Box display="flex" width="100%" justifyContent="center">
                        <Box
                          display="flex"
                          mt="32px"
                          maxWidth="416px"
                          flexDirection="column"
                          gap="16px"
                          width="100%"
                        >
                          <FormSubmitButton
                            disabled={
                              !isValid ||
                              !isDirty ||
                              isClosed ||
                              isAbandon ||
                              (isActive && paymentStarted) ||
                              isFormSubmitting
                            }
                            buttonIcon={<SaveIcon />}
                            buttonText="Save"
                            onClick={handleSubmit(onSubmitForm)}
                          />
                          <FormCancelButton disabled={!isDirty} onClick={handleCancelChanges} />
                        </Box>
                      </Box>
                    )}
                  </Box>

                  {(canEdit || canDelete) && (
                    <Box display="flex" flexDirection="column" width="100%" flexBasis="200px">
                      <Typography className={styles.classes.fieldHeadline}>Actions</Typography>

                      {canEdit && isDraft && (
                        <Box mt="16px">
                          <CommonButton
                            type="warning"
                            buttonText="submit"
                            buttonIcon={<SaveIcon />}
                            onButtonClick={handleSubmitCase}
                            disabled={!isValid || isDirty || !isDraft || isFormSubmitting}
                          />
                        </Box>
                      )}

                      {canEdit && (
                        <Box mt="24px">
                          <CommonButton
                            onButtonClick={handleResetClientAccount}
                            type="success"
                            buttonText="send app invite"
                            buttonIcon={<MobileScreenShareIcon />}
                            disabled={isAbandon || !isSendAppInviteAllowed}
                          />
                        </Box>
                      )}

                      {canEdit && (
                        <Box mt="16px" display="none">
                          <CommonButton
                            type="info"
                            buttonText="issue virtual card"
                            buttonIcon={<CreditCardIcon />}
                            disabled
                          />
                        </Box>
                      )}

                      {canEdit && (
                        <Box mt="16px" display="none">
                          <CommonButton
                            type="info"
                            buttonText="issue physical card"
                            buttonIcon={<CreditCardIcon />}
                            disabled
                          />
                        </Box>
                      )}

                      {canEdit && (
                        <Box mt="16px" display="none">
                          <CommonButton
                            type="info"
                            buttonText="request document"
                            buttonIcon={<DocumentScannerIcon />}
                            disabled
                          />
                        </Box>
                      )}

                      {canEdit && (
                        <Box mt="16px" display="none">
                          <CommonButton
                            type="info"
                            buttonText="request signature"
                            buttonIcon={<DrawIcon />}
                            disabled
                          />
                        </Box>
                      )}

                      {canEdit && (
                        <Box mt="16px" display="none">
                          <CommonButton
                            type="info"
                            buttonText="request data"
                            buttonIcon={<RuleIcon />}
                            disabled
                          />
                        </Box>
                      )}

                      {canEdit && (
                        <Box mt="16px" display="none">
                          <CommonButton
                            type="success"
                            buttonText="add deduction"
                            buttonIcon={<ReceiptIcon />}
                            disabled
                          />
                        </Box>
                      )}

                      {canEdit && (
                        <Box mt="16px" display="none">
                          <CommonButton
                            type="success"
                            buttonText="process settlement"
                            buttonIcon={<PaymentsIcon />}
                            disabled
                          />
                        </Box>
                      )}

                      {canEdit && (
                        <Box mt="16px">
                          <CommonButton
                            onButtonClick={() => setIsAbandonCasePopupOpen(true)}
                            type="error"
                            buttonText="abandon"
                            buttonIcon={<DoNotDisturbIcon />}
                            disabled={
                              !isValid || isDirty || (!canAbandon as boolean) || isFormSubmitting
                            }
                          />
                        </Box>
                      )}

                      {canDelete && (
                        <Box mt="16px">
                          <CommonButton
                            type="error"
                            buttonText="Delete"
                            buttonIcon={<DeleteForeverIcon />}
                            disabled={!isDraft}
                            onButtonClick={() => setIsDeleteCasePopupOpen(true)}
                          />
                        </Box>
                      )}

                      {/* <Box mt="16px">
                      <CommonButton
                        type="error"
                        buttonText="archive"
                        buttonIcon={<ArchiveIcon />}
                        disabled
                      />
                    </Box> */}
                    </Box>
                  )}
                </Box>
              </form>
            </FormProvider>
            <Popup
              isOpen={isAbandonCasePopupOpen}
              headlineText={POPUP_DESCRIPTIONS.caseAbandon.headlineText}
              contentText={POPUP_DESCRIPTIONS.caseAbandon.contentText}
              onMainButtonClick={handleAbandonCase}
              onSecondaryButtonClick={() => setIsAbandonCasePopupOpen(false)}
            />
            <Popup
              isOpen={isDeleteCasePopupOpen}
              headlineText={POPUP_DESCRIPTIONS.caseDelete.headlineText}
              contentText={POPUP_DESCRIPTIONS.caseDelete.contentText}
              onMainButtonClick={handleDeleteCase}
              onSecondaryButtonClick={() => setIsDeleteCasePopupOpen(false)}
            />
          </Box>
        </>
      )}
    </>
  );
};

export default CasesEditFormContainer;
